import { Skeleton } from '@shared/components/shadcn';
import type { NextDrawItem } from '../../../lottery-results/components/NextDraw/types';
import { LotteryPod } from '../../LotteryPod/LotteryPod';

type LotteryCategoryContentProps = {
  isLoading: boolean;
  lotteries: NextDrawItem[];
  skeletonItems?: number;
  emptyMessage?: React.ReactNode;
};

/**
 * LotteryCategoryContent is a shared component that displays a list of lottery categories.
 * While loading, it displays a skeleton UI. Optionally, it can display an empty message if no categories are available.
 *
 * @param isLoading - Indicates whether the component is currently loading data.
 * @param lotteries - An array of lottery categories to display.
 * @param skeletonItems - (Optional) The number of skeleton items to display while loading, use to avoid layout shifts.
 * @param emptyMessage - (Optional) A message to display when no lotteries are available for category.
 * @returns A React component that displays a list of lottery categories.
 */
function LotteryCategoryContent({ isLoading, lotteries, skeletonItems = 4, emptyMessage }: LotteryCategoryContentProps) {
  if (isLoading) return Array.from({ length: skeletonItems }, (_, i) => <Skeleton key={i} className="w-full h-31.5" />);
  if (emptyMessage && lotteries.length === 0) return emptyMessage;

  return lotteries.map((lottery, index) => {
    return <LotteryPod key={lottery.lotteryName} draw={lottery} data-track-container="Lottery" data-track-position={index} data-track-product={lottery.lotteryName} />;
  });
}

export { LotteryCategoryContent };
